/**
 * ProjectileManager - Manages all projectiles in the game
 */
import * as THREE from 'three';

export class ProjectileManager {
  /**
   * Create a new ProjectileManager
   * @param {Object} options - Options
   * @param {THREE.Scene} options.scene - The Three.js scene
   * @param {Object} options.combatManager - Optional combat manager for damage handling
   * @param {Object} options.socket - Socket reference for server communication
   */
  constructor(options = {}) {
    if (!options.scene) {
      console.error('ProjectileManager requires a scene');
      this.scene = null;
    } else if (typeof options.scene.add === 'function') {
      this.scene = options.scene;
    } else if (options.scene.scene && typeof options.scene.scene.add === 'function') {
      this.scene = options.scene.scene;
    } else {
      console.error('Invalid scene passed to ProjectileManager');
      this.scene = null;
    }
    
    this.combatManager = options.combatManager;
    this.socket = options.socket;
    this.projectiles = [];
    this.maxProjectiles = 50;
    
    // Initialize projectile pools for different types
    this.projectilePools = {
      magic: [],
      arrow: []
    };
    
    // Pre-create some projectiles for each type
    this._initializeProjectilePools();
    
    // Keep track of active players for collision detection
    this.players = new Map();
    
    // Cache frequently used values
    this.collisionRadiusSquared = 2.25; // 1.5^2
    this.velocityMultiplier = 0;
    
    console.log('ProjectileManager initialized');
  }

  /**
   * Initialize the manager fully after scene is ready
   * @param {THREE.Scene} scene - The THREE.js scene
   */
  initialize(scene) {
    if (scene) {
      this.scene = scene;
    }
    
    // Register socket event handlers if needed
    this._registerSocketEvents();
    
    console.log('ProjectileManager fully initialized');
  }

  /**
   * Register socket event handlers
   * @private
   */
  _registerSocketEvents() {
    if (!this.socket) {
      console.warn('No socket available for ProjectileManager');
      return;
    }
    
    console.log('Registering projectile socket events');
    
    // Listen for projectile spawn events from server
    this.socket.on('projectileSpawn', (data) => {
      console.log('Received projectile spawn event from server:', data);
      this.addProjectile(data);
    });
    
    // Listen for projectile hit confirmations
    this.socket.on('projectileHitConfirm', (data) => {
      console.log('Server confirmed projectile hit:', data);
      if (data.enemyId && data.damage) {
        // Show hit effect
        const position = data.position || { x: 0, y: 1, z: 0 };
        const effect = {
          position: position,
          type: data.type || 'arrow'
        };
        this.createHitEffect(effect);
      }
    });
  }

  _initializeProjectilePools() {
    // Pre-create 10 projectiles of each type
    for (let i = 0; i < 10; i++) {
      this._createPooledProjectile('magic');
      this._createPooledProjectile('arrow');
    }
  }

  _createPooledProjectile(type) {
    const geometry = type === 'magic' ? 
      new THREE.SphereGeometry(0.2, 8, 8) : 
      new THREE.CylinderGeometry(0.1, 0.1, 0.5, 8);
    
    const material = new THREE.MeshBasicMaterial({
      color: type === 'magic' ? 0x00bbff : 0xff3300,
      transparent: true,
      opacity: 0.8
    });
    
    const mesh = new THREE.Mesh(geometry, material);
    mesh.visible = false;
    this.scene.add(mesh);
    
    this.projectilePools[type].push({
      mesh,
      geometry,
      material,
      isActive: false
    });
  }

  /**
   * Add a new projectile
   * @param {Object} projectile - Projectile configuration
   * @returns {Object|null} The created projectile object or null if creation failed
   */
  addProjectile(projectile) {
    if (!this.scene) {
      console.error('Cannot add projectile: scene not available');
      return null;
    }
    
    try {
      // Check if we're at the projectile limit
      if (this.projectiles.length >= this.maxProjectiles) {
        // Remove oldest projectile to make room
        this.removeProjectile(this.projectiles[0]);
      }
      
      // Get or create a pooled projectile
      const pooledProjectile = this._getPooledProjectile(projectile.type);
      if (!pooledProjectile) return null;
      
      // Configure the pooled projectile
      pooledProjectile.mesh.visible = true;
      pooledProjectile.mesh.position.set(
        projectile.position.x,
        projectile.position.y,
        projectile.position.z
      );
      
      // Set proper rotation based on velocity
      if (projectile.velocity.x !== 0 || projectile.velocity.z !== 0) {
        pooledProjectile.mesh.lookAt(
          projectile.position.x + projectile.velocity.x,
          projectile.position.y + projectile.velocity.y,
          projectile.position.z + projectile.velocity.z
        );
      }
      
      // Create projectile object using pooled resources
      const newProjectile = {
        id: projectile.id || `proj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        mesh: pooledProjectile.mesh,
        position: { ...projectile.position },
        velocity: { ...projectile.velocity },
        damage: projectile.damage || 25,
        type: projectile.type,
        sourceId: projectile.sourceId,
        targetId: projectile.targetId,
        createdAt: Date.now(),
        lifetime: projectile.lifetime * 1000,
        elapsedTime: 0,
        isEnemyProjectile: projectile.isEnemyProjectile,
        isPlayerProjectile: projectile.isPlayerProjectile,
        pooled: true
      };
      
      this.projectiles.push(newProjectile);
      return newProjectile;
    } catch (error) {
      console.error('Error creating projectile:', error);
      return null;
    }
  }

  /**
   * Get a projectile by its ID
   * @param {string} id - The ID of the projectile to find
   * @returns {Object|null} The projectile object or null if not found
   */
  getProjectile(id) {
    return this.projectiles.find(p => p.id === id) || null;
  }

  _getPooledProjectile(type) {
    // Find an inactive projectile in the pool
    const pool = this.projectilePools[type];
    const inactiveProjectile = pool.find(p => !p.isActive);
    
    if (inactiveProjectile) {
      inactiveProjectile.isActive = true;
      return inactiveProjectile;
    }
    
    // If no inactive projectiles, create a new one
    this._createPooledProjectile(type);
    return pool[pool.length - 1];
  }

  /**
   * Update all projectiles
   * @param {number} deltaTime - Time elapsed since last update
   */
  update(deltaTime) {
    if (!this.scene || this.projectiles.length === 0) return;
    
    // Get the latest enemies from the EnemyManager if it exists
    const enemies = window.game && window.game.enemyManager ? window.game.enemyManager.getEnemies() : null;
    
    // Cache current time to avoid multiple Date.now() calls
    const currentTime = Date.now();
    
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const projectile = this.projectiles[i];
      
      // Check lifetime and destroy if exceeded - enforce 3 second max lifetime
      projectile.elapsedTime = (projectile.elapsedTime || 0) + deltaTime * 1000;
      if (projectile.elapsedTime > projectile.lifetime || projectile.elapsedTime > 3000) {
        this.removeProjectile(i);
        continue;
      }
      
      // Calculate velocity multiplier once per frame
      const velocityMultiplier = deltaTime;
      
      // Update position based on velocity with optimized calculations
      projectile.position.x += projectile.velocity.x * velocityMultiplier;
      projectile.position.y += projectile.velocity.y * velocityMultiplier;
      projectile.position.z += projectile.velocity.z * velocityMultiplier;
      
      // Update mesh position if it exists
      if (projectile.mesh) {
        projectile.mesh.position.copy(projectile.position);
        
        // Optimize pulsing animation calculations
        if ((projectile.isEnemyProjectile || projectile.isPlayerProjectile) && projectile.mesh.userData.pulseData) {
          const pulse = projectile.mesh.userData.pulseData;
          const pulseValue = Math.sin(projectile.elapsedTime * 0.005 * pulse.pulseSpeed) * pulse.pulseAmount + pulse.baseScale;
          projectile.mesh.scale.setScalar(pulseValue);
        }
        
        // Reduce particle trail frequency for better performance
        const trailChance = projectile.isPlayerProjectile ? 0.05 : (projectile.isEnemyProjectile ? 0.02 : 0.01);
        if (Math.random() < trailChance) {
          this.createTrailingParticle(projectile);
        }
      }
      
      // Check for collisions with players if this is an enemy projectile
      if (projectile.sourceId && projectile.sourceId.startsWith('enemy_') && this.players.size > 0) {
        let collision = false;
        let hitPlayerId = null;
        
        // Use squared distance for faster collision checks
        const collisionRadiusSquared = 2.25; // 1.5^2
        
        // Check each registered player
        this.players.forEach((player, playerId) => {
          if (!player || !player.position) return;
          
          // Calculate squared distance to player (faster than using Math.sqrt)
          const dx = projectile.position.x - player.position.x;
          const dz = projectile.position.z - player.position.z;
          const distanceSquared = dx * dx + dz * dz;
          
          // If distance is less than collision threshold, we have a hit
          if (distanceSquared < collisionRadiusSquared) {
            collision = true;
            hitPlayerId = playerId;
            
            // Send projectile hit event to server
            if (this.socket) {
              const hitData = {
                sourceId: projectile.sourceId,
                targetId: playerId,
                damage: projectile.damage || 25,
                position: { ...projectile.position },
                velocity: { ...projectile.velocity },
                timestamp: currentTime
              };
              
              this.socket.emit('enemyProjectileHit', hitData);
            }
            
            // Create hit effect
            this.createHitEffect(projectile);
          }
        });
        
        // Remove projectile if collision occurred
        if (collision) {
          this.removeProjectile(i);
        }
      }
    }
    
    // Check for collisions with enemies for player projectiles
    if (enemies && enemies.size > 0) {
      this._checkCollisionsWithEnemies(enemies);
    }
  }

  /**
   * Create a trailing particle effect behind a projectile
   * @param {Object} projectile - The projectile to add a trailing effect for
   */
  createTrailingParticle(projectile) {
    if (!this.scene) return;
    
    try {
      // Enhanced trail effects for all projectiles
      const isEnemyProjectile = projectile.isEnemyProjectile;
      const isPlayerProjectile = projectile.isPlayerProjectile;
      
      // Create small sphere as a particle
      let particleSize;
      
      if (isPlayerProjectile) {
        // Player projectiles get particles based on type
        const isMage = projectile.type === 'magic';
        particleSize = isMage ? 
          (0.08 + Math.random() * 0.08) : // Smaller for mage
          (0.12 + Math.random() * 0.12);  // Larger for rogue/arrow
      } else if (isEnemyProjectile) {
        // Enemy projectiles get medium particles
        particleSize = 0.1 + Math.random() * 0.1;
      } else {
        // Default projectiles get small particles
        particleSize = 0.05 + Math.random() * 0.05;
      }
      
      // Use lower polygon count for better performance
      const segments = 4; // Minimal segments for particles
      const geometry = new THREE.SphereGeometry(particleSize, segments, segments);
      const material = new THREE.MeshBasicMaterial({
        color: projectile.color,
        transparent: true,
        opacity: isPlayerProjectile ? 0.9 : (isEnemyProjectile ? 0.8 : 0.7)
      });
      const particle = new THREE.Mesh(geometry, material);
      
      // Position slightly behind projectile with some randomness
      const randomOffset = isPlayerProjectile ? 0.12 : (isEnemyProjectile ? 0.1 : 0.05);
      particle.position.set(
        projectile.position.x - projectile.velocity.x * randomOffset * Math.random(),
        projectile.position.y - projectile.velocity.y * randomOffset * Math.random(),
        projectile.position.z - projectile.velocity.z * randomOffset * Math.random()
      );
      
      // Add to scene
      this.scene.add(particle);
      
      // Schedule automatic fade out and removal
      const fadeStart = Date.now();
      const fadeDuration = isPlayerProjectile ? 600 : (isEnemyProjectile ? 500 : 400); // Reduced durations
      
      const fadeParticle = () => {
        const elapsed = Date.now() - fadeStart;
        if (elapsed >= fadeDuration) {
          // Remove when fade complete
          if (this.scene) {
            this.scene.remove(particle);
          }
          if (particle.material) {
            particle.material.dispose();
          }
          if (particle.geometry) {
            particle.geometry.dispose();
          }
          return;
        }
        
        // Update opacity
        const baseOpacity = isPlayerProjectile ? 0.9 : (isEnemyProjectile ? 0.8 : 0.7);
        const opacity = baseOpacity * (1 - elapsed / fadeDuration);
        if (particle.material) {
          particle.material.opacity = opacity;
        }
        
        // For enhanced projectiles, add size scaling effect during fade
        if (isPlayerProjectile || isEnemyProjectile) {
          // Player projectiles grow larger than enemy projectiles
          const scale = 1.0 + (elapsed / fadeDuration * (isPlayerProjectile ? 0.7 : 0.5));
          particle.scale.set(scale, scale, scale);
        }
        
        // Schedule next update
        requestAnimationFrame(fadeParticle);
      };
      
      // Start fading
      fadeParticle();
    } catch (error) {
      console.error('Error creating trail particle:', error);
    }
  }

  /**
   * Handle a projectile hitting a player
   * @param {Object} projectile - The projectile that hit
   * @param {Object} player - The player that was hit
   */
  handleHit(projectile, player) {
    // Log the hit
    console.log(`Projectile hit player ${player.id} for ${projectile.damage} damage`);
    
    // Apply damage via combat manager if available
    if (this.combatManager) {
      this.combatManager.applyDamage(player.id, projectile.damage, projectile.sourceId);
    }
    
    // Create hit effect at impact location
    this.createHitEffect(projectile);
  }

  /**
   * Create a hit effect at the projectile's position
   * @param {Object} projectile - The projectile that hit something
   */
  createHitEffect(projectile) {
    if (!this.scene || !projectile || !projectile.position) return;

    // Track active hit effects to prevent too many at once
    if (this.activeHitEffects >= 5) return; // Limit concurrent effects
    
    const isPlayerProjectile = projectile.sourceId && !projectile.sourceId.startsWith('enemy_');
    const isEnemyProjectile = projectile.sourceId && projectile.sourceId.startsWith('enemy_');
    
    // Simplified effect size based on projectile type
    const effectSize = isPlayerProjectile ? 1.5 : (isEnemyProjectile ? 1.2 : 1.0);
    
    // Create a single burst effect instead of multiple particles
    const burstGeometry = new THREE.SphereGeometry(effectSize, 8, 8);
    const burstMaterial = new THREE.MeshBasicMaterial({
      color: projectile.color || 0xff0000,
      transparent: true,
      opacity: 0.8
    });
    
    const burst = new THREE.Mesh(burstGeometry, burstMaterial);
    burst.position.copy(projectile.position);
    this.scene.add(burst);
    
    // Increment counter
    this.activeHitEffects++;
    
    // Simplified animation with fewer steps
    let scale = 0.1;
    let opacity = 0.8;
    
    // Growth rates: player > enemy > default
    const growthRate = isPlayerProjectile ? 0.5 : (isEnemyProjectile ? 0.4 : 0.3);
    
    // Duration: player > enemy > default
    const duration = isPlayerProjectile ? 300 : (isEnemyProjectile ? 250 : 200);
    
    const startTime = Date.now();
    
    const animateBurst = () => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= duration) {
        // Clean up
        this.scene.remove(burst);
        burstGeometry.dispose();
        burstMaterial.dispose();
        
        // Decrement counter
        this.activeHitEffects--;
        return;
      }
      
      // Calculate progress
      const progress = elapsed / duration;
      
      // Update scale and opacity based on progress
      scale = 0.1 + (progress * growthRate * (isPlayerProjectile ? 5 : (isEnemyProjectile ? 4 : 3)));
      opacity = 0.8 * (1 - progress);
      
      burst.scale.set(scale, scale, scale);
      burstMaterial.opacity = opacity;
      
      requestAnimationFrame(animateBurst);
    };
    
    animateBurst();
  }

  /**
   * Remove a projectile by index or object reference
   * @param {number|Object} indexOrProjectile - Index in the projectiles array or projectile object
   */
  removeProjectile(indexOrProjectile) {
    let index = indexOrProjectile;
    if (typeof indexOrProjectile === 'object') {
      index = this.projectiles.indexOf(indexOrProjectile);
      if (index === -1) return;
    }
    
    const projectile = this.projectiles[index];
    if (!projectile) return;
    
    // Return to pool if it's a pooled projectile
    if (projectile.pooled) {
      projectile.mesh.visible = false;
      const pool = this.projectilePools[projectile.type];
      const pooledProjectile = pool.find(p => p.mesh === projectile.mesh);
      if (pooledProjectile) {
        pooledProjectile.isActive = false;
      }
    } else {
      // Clean up non-pooled projectiles
      if (projectile.mesh) {
        this.scene.remove(projectile.mesh);
        if (projectile.mesh.geometry) projectile.mesh.geometry.dispose();
        if (projectile.mesh.material) projectile.mesh.material.dispose();
      }
    }
    
    this.projectiles.splice(index, 1);
  }

  /**
   * Register a player for collision detection
   * @param {string} playerId - Player ID
   * @param {Object} playerData - Player data with position
   */
  registerPlayer(playerId, playerData) {
    if (playerId && playerData && playerData.position) {
      this.players.set(playerId, playerData);
    }
  }

  /**
   * Update a player's position
   * @param {string} playerId - Player ID
   * @param {Object} position - New position
   */
  updatePlayerPosition(playerId, position) {
    if (playerId && this.players.has(playerId) && position) {
      const player = this.players.get(playerId);
      player.position = position;
    }
  }

  /**
   * Unregister a player
   * @param {string} playerId - Player ID to unregister
   */
  unregisterPlayer(playerId) {
    if (playerId) {
      this.players.delete(playerId);
    }
  }

  /**
   * Clear all projectiles
   */
  clear() {
    // Remove all projectiles
    while (this.projectiles.length > 0) {
      this.removeProjectile(this.projectiles[0]);
    }
  }

  /**
   * Dispose resources
   */
  dispose() {
    this.clear();
    this.players.clear();
  }

  /**
   * Check for collisions between projectiles and enemies
   * @param {Object} enemies - Map of enemies to check against
   * @private
   */
  _checkCollisionsWithEnemies(enemies) {
    if (!enemies || enemies.size === 0) return;
    
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const projectile = this.projectiles[i];
      
      // Skip enemy projectiles - they can't hit enemies
      if (projectile.sourceId && projectile.sourceId.startsWith('enemy_')) continue;
      
      // Check each enemy for collision
      let collision = false;
      let hitEnemy = null;
      
      enemies.forEach((enemy, enemyId) => {
        if (collision) return; // Skip if we already found a collision for this projectile
        
        // Skip if enemy doesn't have a position
        if (!enemy || !enemy.position) return;
        
        // Calculate distance between projectile and enemy
        const distance = Math.sqrt(
          Math.pow(projectile.position.x - enemy.position.x, 2) +
          Math.pow(projectile.position.z - enemy.position.z, 2)
        );
        
        // Use larger collision radius for more reliable hit detection
        // Increased to 2.0 for much more reliable hits
        const collisionRadius = 2.0;
        
        // If distance is less than collision radius, we have a hit
        if (distance < collisionRadius) {
          collision = true;
          hitEnemy = enemy;
          
          // Calculate damage based on projectile type
          let damage = projectile.damage || 25; // Use projectile's own damage value with fallback to 25
          
          console.log(`********************`);
          console.log(`PROJECTILE HIT: Player projectile hit enemy ${enemyId} for ${damage} damage`);
          console.log(`Distance: ${distance.toFixed(2)}, Collision radius: ${collisionRadius}`);
          console.log(`Enemy position: (${enemy.position.x.toFixed(2)}, ${enemy.position.z.toFixed(2)})`);
          console.log(`Projectile position: (${projectile.position.x.toFixed(2)}, ${projectile.position.z.toFixed(2)})`);
          console.log(`********************`);
          
          // Send hit event to server - ONLY SEND ONE EVENT PER HIT
          if (this.socket) {
            // Send projectileHit event with full details
            this.socket.emit('projectileHit', {
              projectileId: projectile.id || `proj_${Date.now()}`,
              targetId: enemyId,
              damage: damage,
              position: { ...projectile.position },
              timestamp: Date.now()
            });
            
            // Log what was sent
            console.log(`Sent projectileHit event for ${damage} damage to enemy ${enemyId}`);
          }
          
          // Create hit effect
          this.createHitEffect(projectile);
          
          // Update enemy health locally for immediate feedback
          if (hitEnemy) {
            const oldHealth = hitEnemy.health || 100;
            hitEnemy.health = Math.max(0, oldHealth - damage);
            console.log(`Local enemy health update: ${oldHealth} -> ${hitEnemy.health}`);
            
            // Flash the enemy red on hit for visual feedback
            if (hitEnemy.mesh) {
              const originalMaterial = hitEnemy.mesh.material ? hitEnemy.mesh.material.clone() : null;
              if (hitEnemy.mesh.material) {
                hitEnemy.mesh.material.color.set(0xff0000);
                
                // Create a visible hit flash effect at the impact point
                const flashGeometry = new THREE.SphereGeometry(0.5, 8, 8);
                const flashMaterial = new THREE.MeshBasicMaterial({
                  color: 0xff0000,
                  transparent: true,
                  opacity: 0.7
                });
                const flash = new THREE.Mesh(flashGeometry, flashMaterial);
                flash.position.copy(projectile.position);
                
                if (this.scene) {
                  this.scene.add(flash);
                  
                  // Fade out and remove the flash
                  let opacity = 0.7;
                  const fadeInterval = setInterval(() => {
                    opacity -= 0.1;
                    if (opacity <= 0) {
                      clearInterval(fadeInterval);
                      this.scene.remove(flash);
                      flash.geometry.dispose();
                      flash.material.dispose();
                    } else {
                      flash.material.opacity = opacity;
                    }
                  }, 50);
                }
                
                // Reset enemy material after delay
                setTimeout(() => {
                  if (hitEnemy.mesh && hitEnemy.mesh.material && originalMaterial) {
                    hitEnemy.mesh.material.color.copy(originalMaterial.color);
                  }
                }, 150);
              }
            }
            
            // Check if enemy should be killed locally (health <= 0)
            if (hitEnemy.health <= 0) {
              console.log(`Enemy ${enemyId} health reached 0 locally - waiting for server confirmation`);
            }
          }
        }
      });
      
      // Remove projectile if it hit something
      if (collision) {
        this.removeProjectile(i);
      }
    }
  }
}

export default ProjectileManager; 